import type { PresenceStatus } from "@shared/types";

/**
 * Paleta do LFRCORD, tirada da arte da marca (client/public/brand/logo.png).
 *
 * As quatro cores que a identidade define de verdade sao poucas: o fundo
 * `#0A1020`, o azul do balao `#1F4FD8`, o azul claro do losango `#4CA9FF` e o
 * navy de profundidade `#10265C`. Todo o resto — fundos intermediarios,
 * bordas e a escada de texto — e uma progressao construida dentro dessa mesma
 * familia, para que nenhuma superficie da interface escape do sistema
 * cromatico da marca.
 */
export const C = {
  /** Fundo da aplicacao — o mesmo navy do fundo da arte. */
  app: "#0A1020",
  /** Barra lateral e painel de membros. */
  panel: "#0C1428",
  /** Area de conversa / voz. */
  stage: "#101A33",
  /** Rodape do usuario na lateral. */
  panelFoot: "#0A1020",
  /** Faixa de voz conectada. */
  panelVoice: "#122043",
  /** Fundo de campos e caixas. */
  field: "#0D1526",
  /** Caixa do compositor e barra de controles. */
  raised: "#14203E",
  /** Tile de participante — o navy de profundidade da marca. */
  tile: "#10265C",
  /** Selecionado / hover forte. */
  selected: "#1B2B54",
  hover: "#121B33",
  hoverStrong: "#1B2B54",
  /** Bordas. */
  line: "#1D2C5E",
  lineSoft: "#16224A",
  lineStrong: "#243568",
  lineTile: "#20305F",
  lineRow: "#16224A",
  track: "#2B4183",
  /** Texto. */
  text: "#EAF1FF",
  textStrong: "#DCE6FA",
  textBody: "#C7D3EE",
  textSoft: "#B4C1E0",
  textMuted: "#9BAAD0",
  textDim: "#8794B8",
  textFaint: "#7684A8",
  textLabel: "#647098",
  textLabelDim: "#57628A",
  textGhost: "#454F73",
  idle: "#9BAAD0",
  /** Semantica. */
  accent: "#1F4FD8",
  /** Azul claro do losango: usado para presenca online e realces. */
  green: "#4CA9FF",
  blue: "#4CA9FF",
  red: "#FF4D6D",
  amber: "#E8B34D",
  offline: "#4A5578",
  /** Texto sobre o azul da marca. */
  ink: "#0A1020",
} as const;

/** Caminhos das artes da marca, para a interface nao repetir string solta. */
export const BRAND = {
  /** Simbolo: balao com o "L". Fundo transparente. */
  mark: "/brand/marca.png",
  /** Logo completa, simbolo + palavra. Fundo transparente. */
  full: "/brand/logo.png",
} as const;

export const FONT_SANS = "'Space Grotesk', sans-serif";
export const FONT_MONO = "'IBM Plex Mono', monospace";

/** Converte `#rrggbb` em `rgba(r,g,b,a)` — mesma funcao `hexA` do desenho. */
export function hexA(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export function statusColor(status: PresenceStatus): string {
  if (status === "online") return C.green;
  if (status === "ausente") return C.amber;
  if (status === "ocupado") return C.red;
  return C.offline;
}

export function statusLabel(status: PresenceStatus): string {
  if (status === "online") return "Online agora";
  if (status === "ausente") return "Ausente";
  if (status === "ocupado") return "Ocupado";
  return "Offline";
}

export function initialOf(name: string): string {
  return (name.trim()[0] ?? "?").toUpperCase();
}

/** Formata `createdAt` no padrao do desenho: "hoje as 19:02", "ontem as 15:20". */
export function formatStamp(createdAt: number, now = Date.now()): string {
  const date = new Date(createdAt);
  const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date(now)) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return `hoje as ${time}`;
  if (days === 1) return `ontem as ${time}`;
  if (days < 7) {
    const weekday = date.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
    return `${weekday} as ${time}`;
  }
  return `${date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} as ${time}`;
}

/** Rotulo curto usado na lista de diretas ("19:02", "sex 18:32"). */
export function formatShortStamp(createdAt: number, now = Date.now()): string {
  return formatStamp(createdAt, now).replace("hoje as ", "").replace(" as ", " ");
}
